import React, { useState } from "react";

import { UPLOAD_DELETE_ENABLED } from "./shared";
import UploadStatusDialog from "./UploadStatusDialog";
import {
  apiErrorMessage,
  useAccountPlanActions,
  useAccountPlanFile,
} from "../../Hooks/useAccountPlan";

interface Props {
  accountId: string;
  fileId: string;
  /** Shown until the version loads. */
  filename: string;
  /** The account's AE (or an admin): may try again, override or delete. */
  canEdit: boolean;
  onClose: () => void;
  /** Something changed (deleted, retried, overridden): refresh the list behind. */
  onChanged: () => void;
  onOpenPlan: () => void;
}

/**
 * The upload dialog for a version opened from a list (Plans → "needs fixing" /
 * "failed"): why it wasn't used, and Try again / This is the right plan / Delete
 * this upload for the AE (spec VAL-28). Loads the version itself; the dialog is the
 * same one Version history opens.
 */
const VersionUploadDialog: React.FC<Props> = ({
  accountId,
  fileId,
  filename,
  canEdit,
  onClose,
  onChanged,
  onOpenPlan,
}) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retried, setRetried] = useState(false);
  const { file, reload } = useAccountPlanFile(accountId, fileId, retried);
  const { retryPlanUpload, overridePlanVersion, deletePlanVersion } =
    useAccountPlanActions();

  /** Run one action on the version, then refresh; `close` once it's done with. */
  const act = async (
    fn: () => Promise<unknown>,
    fallback: string,
    close = false
  ) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChanged();
      if (close) onClose();
      else await reload();
    } catch (err) {
      setError(apiErrorMessage(err, fallback));
    } finally {
      setBusy(false);
    }
  };

  return (
    <UploadStatusDialog
      open
      file={file}
      filename={file?.filename || filename}
      canEdit={canEdit}
      busy={busy}
      error={error}
      onClose={onClose}
      onWithdraw={() => undefined}
      onRetry={() => {
        setRetried(true);
        void act(
          () => retryPlanUpload(accountId, fileId),
          "Couldn't try again."
        );
      }}
      onOpenPlan={onOpenPlan}
      // F-UF-F03: a flagged upload is confirmed on Confirm plan (with the
      // "I've checked this…" tick), which "Open plan" leads to.
      onOverride={reason =>
        void act(
          () => overridePlanVersion(accountId, fileId, reason),
          "Couldn't use this version.",
          true
        )
      }
      onDiscard={
        UPLOAD_DELETE_ENABLED
          ? () =>
              void act(
                () => deletePlanVersion(accountId, fileId),
                "Couldn't remove this version.",
                true
              )
          : undefined
      }
    />
  );
};

export default VersionUploadDialog;
